'use client';

import { ReactNode, useState, useEffect, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { useGetMessagesForConversationQuery, MESSAGES_CACHE_PARAMS } from '@/api/features/messaging';
import { usePrefetchCoordinator } from '@/core/hooks/usePrefetchCoordinator';
import { SocketProvider } from '../../../core/providers/SocketProvider';

import LoadingSystem from '@/shared/components/loading/LoadingSystem';
import { E2EHealthPanelConnected } from '@/shared/components/e2e';
import { useAppDispatch, useAppSelector } from '@/core/store/hooks';
import { setConversationsLoadDegraded, selectBootId, selectBootStatus } from '@/core/store/slices/bootSlice';
import { config } from '@/config/config';
import { apiLogger } from '@/core/utils/logger';
import { addBreadcrumb } from '@/shared/observability/breadcrumb';

/**
 * Max time to wait for conversations before proceeding anyway.
 *
 * Set to 15s to be above the dev API timeout (12s) while still providing
 * reasonable recovery time. In production (API timeout ~24s), this means
 * we intentionally unblock early and show a "degraded" state in the UI
 * rather than waiting the full API timeout. This is a deliberate tradeoff:
 * - Better UX: Users see actionable UI faster
 * - Clear feedback: UI shows "Unable to load" vs misleading "No conversations"
 * - Observability: Timeout events are logged to Sentry for monitoring
 */
const CONVERSATION_LOAD_TIMEOUT_MS = 15000;

/** Module-level constant for empty tagIds array - stable reference */
const EMPTY_TAG_IDS: string[] = [];

interface DashboardDataBoundaryProps {
  children: ReactNode;
}

/**
 * Dashboard Data Boundary
 *
 * Provides real-time providers and handles conversation prefetching.
 *
 * NOTE: Critical data gating (auth, csrf, profile, org) is handled by
 * DashboardBootstrap. This component assumes those are already ready
 * and focuses on:
 * 1. Conversation prefetching (blocks INITIAL render only, with timeout)
 * 2. Message prefetching for current conversation
 * 3. Real-time providers (Socket, Presence)
 *
 * After initial load, refetches happen in background without blocking.
 */
export default function DashboardDataBoundary({ children }: DashboardDataBoundaryProps) {
  const params = useSearchParams();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const bootId = useAppSelector(selectBootId);
  const bootStatus = useAppSelector(selectBootStatus);
  const isMessagingRoute = pathname?.startsWith('/dashboard');
  const viewParam = (params.get('view') as 'all' | 'assigned' | 'unassigned' | null) || 'all';

  // Timeout fallback - don't block forever on conversation load
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  // Memoize conversation query args to prevent RTKQ cache busting on re-renders
  // NOTE: These args must match ConversationsContainer's initial args for RTKQ to dedupe.
  // RTKQ uses JSON serialization for cache keys, so [] === [] as long as values match.
  // If you see duplicate requests in network traces, verify ConversationsContainer uses:
  //   - search: '' (debouncedSearchQuery initial value)
  //   - tagIds: [] (selectedTags initial value)
  //   - limit: 20 (DEFAULT_LIMIT constant)
  const conversationQueryArgs = useMemo(
    () => ({
      search: '',
      tagIds: EMPTY_TAG_IDS,
      view: viewParam,
      limit: 20,
      page: 0,
      includeArchived: false,
      light: true,
    }),
    [viewParam]
  );

  const { isSuccess: convReady, isLoading: convLoading, isError: convError, data: conversationsData } =
    useGetConversationsQuery(conversationQueryArgs, { refetchOnMountOrArgChange: 5 * 60 });

  const hasConversationData = !!(conversationsData && conversationsData.items && conversationsData.items.length > 0);
  const convResolved = convReady || convError || loadTimedOut;

  // Timeout effect with observability
  useEffect(() => {
    if (convResolved) return;

    const timeoutId = setTimeout(() => {
      setLoadTimedOut(true);

      // Mark as degraded if we timed out without data
      // This tells the UI to show "Unable to load" instead of "No conversations"
      if (!hasConversationData) {
        dispatch(setConversationsLoadDegraded(true));
      }

      // Add Sentry breadcrumb for observability
      addBreadcrumb({
        category: 'boot',
        message: 'Conversations load timeout',
        level: 'warning',
        data: {
          boot_id: bootId,
          boot_status: bootStatus,
          route: 'dashboard',
          view: viewParam,
          had_data_at_timeout: hasConversationData,
          timeout_ms: CONVERSATION_LOAD_TIMEOUT_MS,
        },
      });

      // Log in development
      if (config.env !== 'production') {
        apiLogger.warn('[DashboardDataBoundary] Conversations load timeout', {
          bootId,
          bootStatus,
          view: viewParam,
          hadDataAtTimeout: hasConversationData,
          timeoutMs: CONVERSATION_LOAD_TIMEOUT_MS,
        });
      }
    }, CONVERSATION_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [dispatch, bootId, bootStatus, viewParam, convResolved, hasConversationData]);

  // Clear degraded state if data arrives (e.g., retry succeeded)
  useEffect(() => {
    if (hasConversationData) {
      dispatch(setConversationsLoadDegraded(false));
    }
  }, [hasConversationData, dispatch]);

  // Determine initial conversation to prefetch messages for
  const urlConversationId = params.get('conversation');
  const firstActiveConversationId = conversationsData?.items?.find?.((c) => !c.isArchived)?.id as string | undefined;
  const initialConversationId = (urlConversationId || firstActiveConversationId) as string | undefined;
  // Skip prefetch if conversationId is '__COMPOSE__' (compose mode) or invalid
  const isValidConversationId = initialConversationId && initialConversationId !== '__COMPOSE__';
  const canPrefetchMessages = isMessagingRoute && isValidConversationId && (!!urlConversationId || (convReady && !!firstActiveConversationId));
  const shouldSkipMessages = !canPrefetchMessages || !initialConversationId || !isValidConversationId;

  // Memoize messages query args to prevent RTKQ cache busting on re-renders
  // Note: When skip=true, RTK Query ignores the args entirely, so we use a
  // placeholder that won't cause issues. The query only runs when we have a valid ID.
  // IMPORTANT: Must use MESSAGES_CACHE_PARAMS for cache consistency with WebSocket updates
  const messagesQueryArgs = useMemo(
    () => ({
      parentId: initialConversationId || '',
      ...MESSAGES_CACHE_PARAMS,
    }),
    [initialConversationId]
  );

  // Prefetch messages for initial conversation so chat view is ready
  useGetMessagesForConversationQuery(messagesQueryArgs, {
    skip: shouldSkipMessages,
  });

  // Use coordinated prefetch for teams data (no duplicates)
  usePrefetchCoordinator({
    enabled: true, // DashboardBootstrap guarantees we're ready
    priority: 'low',
    section: 'teams'
  });

  // Block until initial conversations resolve, then allow render.
  // Timeout/error unblocks and falls back to degraded UI.
  const shouldBlockOnConversations = convLoading && !convResolved;

  if (shouldBlockOnConversations) {
    return <LoadingSystem show={true} message="Loading conversations..." size="large" fullScreen />;
  }

  // Render with real-time providers
  // Note: PresenceProvider removed - presence is handled by PresenceInitializer in SocketProvider
  return (
    <SocketProvider>
      {children}
      {/* E2E Health Panel - only visible in dev/test mode */}
      <E2EHealthPanelConnected />
    </SocketProvider>
  );
}
